import { forwardRef, MouseEvent } from "react";
import styled from "styled-components";

export interface CloseButtonProps {
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  /** 表示する Material Symbols 名（default: "close"） */
  icon?: string;
  /** スクリーンリーダー向けラベル（default: "閉じる"） */
  ariaLabel?: string;
  /** 親要素から追加スタイルを当てる際に使う */
  className?: string;
  /** 無効状態 */
  disabled?: boolean;
}

const StyledButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  border-radius: 6px;
  padding: 0;
  transition: background-color 0.2s ease;

  .material-symbols-rounded {
    font-size: 20px;
    color: var(--soremo-light-gray);
    transition: color 0.2s ease;
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);

    .material-symbols-rounded {
      color: var(--soremo-gray);
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

/**
 * 共通の閉じるアイコンボタン。
 *
 * モーダル右上、検索バー、ドロワー等で「閉じる/解除」操作に使う。
 * - ホバー時は背景がほんのり暗くなり、アイコン色が一段濃くなる
 * - 色は変えない（青への色変化はしない）
 *
 * 配置（position / top / right 等）は利用側で `styled(CloseButton)` の
 * ラッパーで指定する。本コンポーネントは見た目だけを担う。
 */
const CloseButton = forwardRef<HTMLButtonElement, CloseButtonProps>(
  (
    {
      onClick,
      icon = "close",
      ariaLabel = "閉じる",
      className,
      disabled = false,
    },
    ref,
  ) => {
    return (
      <StyledButton
        ref={ref}
        onClick={onClick}
        aria-label={ariaLabel}
        className={className}
        type="button"
        disabled={disabled}
      >
        <span className="material-symbols-rounded">{icon}</span>
      </StyledButton>
    );
  },
);

CloseButton.displayName = "CloseButton";

export default CloseButton;
